import React from 'react';
import { Alert, DatePicker, Radio, Textarea, Tooltip } from 'aha-components';
import { useTranslation } from 'react-i18next';
import { useMultiplePricesStore } from '@/hooks/useMultiplePricesStore';
import { isMobile } from '@/biz/tool';

/**
 * 达人报价时的档期冲突表单。
 * 数据由报价 store 持有，以便 Accept offer、Share rate 和 Counter offer
 * 三种抽屉在最终提交时读取同一份数据。
 */
export default function CreatorScheduleForm() {
  const { t } = useTranslation();
  const { creatorScheduleConflict, setCreatorScheduleConflict } =
    useMultiplePricesStore();
  const { status, unavailableStartDate, unavailableEndDate, notes } =
    creatorScheduleConflict;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const hasIncompleteDateRange =
    status === 'yes' && !!unavailableStartDate && !unavailableEndDate;

  const selectConflict = (hasConflict: boolean) => {
    setCreatorScheduleConflict({
      ...creatorScheduleConflict,
      status: hasConflict ? 'yes' : 'no',
      // Yes 默认当天；No 不提交残留日期，但保留 notes 方便误触后恢复。
      unavailableStartDate: hasConflict
        ? (unavailableStartDate ?? today)
        : null,
      unavailableEndDate: hasConflict ? (unavailableEndDate ?? today) : null
    });
  };

  return (
    <section className="w-full flex flex-col gap-3">
      <div className="flex items-center gap-1">
        <h3 className="m-0 text-[#292524] text-base font-semibold leading-6">
          {t('price.fields.schedule_conflict.title', {
            defaultValue: 'Do you have any upcoming schedule conflicts?'
          })}
        </h3>
        <Tooltip
          position="top"
          trigger={isMobile() ? 'click' : 'hover'}
          width={240}
          content={t('price.fields.schedule_conflict.tooltip', {
            defaultValue:
              'Clearer communication upfront would help make the collaboration go more smoothly'
          })}
        >
          <button
            type="button"
            aria-label={t('price.fields.schedule_conflict.tooltip_label', {
              defaultValue: 'About schedule conflicts'
            })}
            className="shrink-0 w-4 h-4 rounded-full border border-[#A8A29E] text-[#78716C] text-[11px] font-semibold leading-none flex items-center justify-center bg-transparent cursor-help"
          >
            ?
          </button>
        </Tooltip>
      </div>
      <div className="grid grid-cols-2 gap-2 sm:gap-3" role="radiogroup">
        {[false, true].map((value) => (
          <label
            key={String(value)}
            onClick={(event) => {
              // 显式处理整个卡片热区；阻止 label 再次转发 click，避免重复触发。
              event.preventDefault();
              selectConflict(value);
            }}
            className={`min-w-0 h-14 sm:h-16 px-4 sm:px-5 rounded-lg border cursor-pointer flex items-center justify-between text-base font-semibold transition-colors ${
              (status === 'yes') === value
                ? 'border-2 border-[#FF5A1F] text-[#292524]'
                : 'border border-[#D6D3D1] text-[#292524]'
            }`}
          >
            <span>
              {value
                ? t('price.fields.schedule_conflict.yes', {
                    defaultValue: 'Yes'
                  })
                : t('price.fields.schedule_conflict.no', {
                    defaultValue: 'No'
                  })}
            </span>
            <Radio
              name="creator-schedule-conflict"
              checked={(status === 'yes') === value}
              onChange={() => selectConflict(value)}
            />
          </label>
        ))}
      </div>

      {status === 'yes' && (
        <>
          <div className="w-full rounded-xl bg-[#FAFAFA] p-3 sm:p-4 flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <div className="text-[#292524] text-base font-semibold">
                {t('price.fields.schedule_conflict.unavailable_dates', {
                  defaultValue: 'Unavailable dates'
                })}
              </div>
              <DatePicker
                mode="range"
                size="large"
                className="w-full"
                rangeDisplayMode="text"
                calendarCount={1}
                allowClear={false}
                minDate={today}
                error={hasIncompleteDateRange}
                // Drawer 在移动端层级较高，避免日历面板被遮挡。
                dropdownStyle={{ zIndex: 10000 }}
                value={{
                  startDate: unavailableStartDate,
                  endDate: unavailableEndDate
                }}
                onChange={({ startDate, endDate }) =>
                  setCreatorScheduleConflict({
                    ...creatorScheduleConflict,
                    unavailableStartDate: startDate,
                    unavailableEndDate: endDate
                  })
                }
              />
              {hasIncompleteDateRange && (
                <div className="text-red-500 text-sm">
                  Please select an end date.
                </div>
              )}
            </div>
            <Textarea
              label={t('price.fields.schedule_conflict.notes', {
                defaultValue: 'Schedule notes (optional)'
              })}
              rows={4}
              className="w-full"
              value={notes}
              placeholder={t(
                'price.fields.schedule_conflict.notes_placeholder',
                {
                  defaultValue: 'Add notes to the brand'
                }
              )}
              onChange={(event) =>
                setCreatorScheduleConflict({
                  ...creatorScheduleConflict,
                  notes: event.target.value
                })
              }
            />
          </div>
          <Alert
            type="info"
            showIcon
            description={t('price.fields.schedule_conflict.alert_description', {
              defaultValue:
                'If this collaboration officially starts and these dates affect content production, you’ll need to submit an extension request within the required timeframe, or your platform credits may be impacted.'
            })}
          />
        </>
      )}
    </section>
  );
}
